/**
 * Server clock drift compensation.
 *
 * Each judge tablet, OBS screen and manager laptop has its own system clock,
 * which can be off from the WFFA server by a few seconds (especially on
 * Samsung tablets where "auto time" was once disabled). When the battle/
 * circle timer is computed locally as `Date.now() - segment_started_at`,
 * any drift translates 1-for-1 into a visible countdown offset.
 *
 * This module:
 *  1. Pings GET /api/stream/server-time on app boot, then every 60 s
 *     (and on tab-visibility regain), measuring the round-trip latency.
 *  2. **Filters out high-latency samples** (>MAX_ACCEPTABLE_RTT_MS) so a
 *     spike on a poor mobile connection can't lock in a multi-second
 *     drift. When every sample in a burst is high-latency, we take the
 *     LOWEST-RTT one only if it's below FALLBACK_MAX_RTT_MS.
 *  3. Runs a short burst of N samples per sync and keeps the one with
 *     the smallest round-trip time — a lightweight NTP-style approach.
 *  4. Computes `drift = server_time_ms - local_time_at_response_midpoint`.
 *  5. Exposes `serverNow()` → `Date.now() + drift` so every consumer can
 *     replace `Date.now()` with this drift-corrected wall clock.
 *
 * Failure is silent: if every sample is too slow (network down, ingress
 * meltdown, ...), we keep the last known drift (or 0). The timer remains
 * usable, just potentially off by a few seconds until the next good sync.
 */

import { BACKEND_URL } from './apiBase';

let _drift = 0;
let _lastSyncAt = 0;
let _lastRtt = Infinity;

const RESYNC_INTERVAL_MS = 60_000;
// Under this RTT the sample is considered high-quality and accepted.
const MAX_ACCEPTABLE_RTT_MS = 400;
// If every sample in a burst exceeds MAX_ACCEPTABLE_RTT_MS, we still take
// the best of them ONLY if it's below this ceiling. Above that we bail out
// entirely and keep the previous drift.
const FALLBACK_MAX_RTT_MS = 1500;
const BURST_SAMPLES = 3;

async function singleSample(apiBase) {
  const t0 = Date.now();
  try {
    const res = await fetch(`${apiBase}/api/stream/server-time`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    const t1 = Date.now();
    const rtt = t1 - t0;
    const serverAtMid = data?.server_time_ms || 0;
    if (!serverAtMid) return null;
    const localAtMid = (t0 + t1) / 2;
    return { drift: serverAtMid - localAtMid, rtt, at: t1 };
  } catch {
    return null;
  }
}

async function syncOnce() {
  const apiBase = BACKEND_URL;
  if (!apiBase) return;
  // Burst-sample: take BURST_SAMPLES back-to-back and pick the one with
  // the lowest RTT (the closest approximation of server time).
  let best = null;
  for (let i = 0; i < BURST_SAMPLES; i += 1) {
    const s = await singleSample(apiBase);
    if (s && (!best || s.rtt < best.rtt)) best = s;
    // Fast exit: if we already got a high-quality sample no need to keep pinging.
    if (best && best.rtt <= MAX_ACCEPTABLE_RTT_MS) break;
  }
  if (!best) return;
  // Reject unreasonably high-latency samples. On a bad 3G a 3-4 s round-trip
  // would inject the same amount of asymmetric error into the drift.
  if (best.rtt > FALLBACK_MAX_RTT_MS) return;
  _drift = best.drift;
  _lastRtt = best.rtt;
  _lastSyncAt = best.at;
}

let _started = false;
function ensureStarted() {
  if (_started || typeof window === "undefined") return;
  _started = true;
  syncOnce();
  setInterval(() => {
    if (!navigator.onLine) return;
    if (Date.now() - _lastSyncAt < RESYNC_INTERVAL_MS / 2) return;
    syncOnce();
  }, RESYNC_INTERVAL_MS);
  window.addEventListener("focus", syncOnce);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncOnce();
  });
  window.addEventListener("online", syncOnce);
}

ensureStarted();

/**
 * Returns the current time in milliseconds since epoch, corrected for the
 * device's drift relative to the WFFA server. Drop-in replacement for
 * `Date.now()` everywhere timer math is involved.
 */
export function serverNow() {
  return Date.now() + _drift;
}

/** Current measured drift in ms (>0 means local clock is BEHIND the server). */
export function currentDrift() {
  return _drift;
}

/** Latest accepted round-trip time in ms. Useful for diagnostics. */
export function currentRtt() {
  return _lastRtt;
}

export default serverNow;
